/**
 * OverflowMenu — THE "…" menu (DESIGN-CALM-01 · Wave 2, Seth 2026-09-22 "go").
 * The Row-actions rule: one primary action visible on a row, everything else
 * behind one small "…" button (Salesforce's row action menu, HubSpot's hover
 * menu). Also the "More filters ▾" menu on list toolbars.
 *
 * Rendered through a portal at a fixed position next to the trigger, so it is
 * safe inside overflow-hidden cards and scrolling lists (Seth's rule: never a
 * dropdown inside overflow-hidden). Closes on outside click, Esc, scroll,
 * resize, blur. Arrow keys move, Enter picks, focus returns to the trigger.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal, Check } from "lucide-react";
import { cn } from "@/lib/cn";

export interface MenuItem {
  key?: string;
  label: string;
  icon?: ReactNode;
  onSelect: () => void;
  /** Rose text — Decline, Delete, Cancel. */
  danger?: boolean;
  disabled?: boolean;
  /** A quiet count after the label (filter menus). */
  count?: number | string | null;
  /** Shows a check mark — the item is the current choice. */
  checked?: boolean;
  /** Greyed out but still clickable (a filter with nothing in it). */
  muted?: boolean;
  /** Native tooltip — the one place a longer explanation may live. */
  title?: string;
}
export type MenuEntry = MenuItem | "divider";

export function OverflowMenu({
  items,
  label = "More",
  align = "end",
  size = "sm",
  className,
  trigger,
}: {
  items: MenuEntry[];
  /** aria-label for the default "…" trigger. */
  label?: string;
  align?: "start" | "end";
  size?: "xs" | "sm";
  className?: string;
  /** A custom trigger (e.g. "More filters ▾"). It receives open state. */
  trigger?: (open: boolean) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left?: number; right?: number; up: boolean } | null>(null);
  const id = useId();

  const close = useCallback((refocus = true) => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const est = Math.min(items.length, 12) * 34 + 12;
    const up = r.bottom + est > window.innerHeight - 8 && r.top > est;
    setPos({
      top: up ? r.top - 4 : r.bottom + 4,
      left: align === "start" ? r.left : undefined,
      right: align === "end" ? window.innerWidth - r.right : undefined,
      up,
    });
  }, [open, align, items.length]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (menuRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); close(); return; }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const els = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]:not(:disabled)') ?? []);
        if (els.length === 0) return;
        e.preventDefault();
        const i = els.indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === "ArrowDown" ? (i + 1) % els.length : (i - 1 + els.length) % els.length;
        els[next]?.focus();
      }
      if (e.key === "Tab") close(false);
    };
    const onAway = () => close(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onAway, true);
    window.addEventListener("resize", onAway);
    window.addEventListener("blur", onAway);
    // first item takes focus so the arrows work at once
    const t = window.setTimeout(() => menuRef.current?.querySelector<HTMLButtonElement>('[role="menuitem"]:not(:disabled)')?.focus(), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onAway, true);
      window.removeEventListener("resize", onAway);
      window.removeEventListener("blur", onAway);
    };
  }, [open, close]);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        aria-label={trigger ? undefined : label}
        title={trigger ? undefined : label}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={cn(
          trigger
            ? "inline-flex items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 rounded-md"
            : cn(
                "inline-flex shrink-0 items-center justify-center rounded-md border border-hairline bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40",
                size === "xs" ? "h-6 w-6" : "h-7 w-7",
                open && "bg-slate-100 text-slate-800",
              ),
          className,
        )}
      >
        {trigger ? trigger(open) : <MoreHorizontal className={size === "xs" ? "h-3.5 w-3.5" : "h-4 w-4"} />}
      </button>
      {open && pos && createPortal(
        <div
          ref={menuRef}
          id={id}
          role="menu"
          data-stay-open=""
          style={{ position: "fixed", top: pos.up ? undefined : pos.top, bottom: pos.up ? window.innerHeight - pos.top : undefined, left: pos.left, right: pos.right, zIndex: 1000 }}
          data-glass="panel"
          className="min-w-[180px] max-w-[300px] overflow-hidden rounded-xl py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {items.map((it, i) =>
            it === "divider" ? (
              <div key={`d${i}`} role="separator" className="my-1 border-t border-hairline" />
            ) : (
              <button
                key={it.key ?? it.label}
                type="button"
                role="menuitem"
                disabled={it.disabled}
                title={it.title}
                onClick={() => { close(); it.onSelect(); }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] focus-visible:outline-none focus-visible:bg-black/[0.04] hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-40",
                  it.danger ? "text-rose-700" : it.muted ? "text-slate-400" : "text-slate-700",
                )}
              >
                {it.icon && <span className="flex h-4 w-4 shrink-0 items-center justify-center [&>svg]:h-3.5 [&>svg]:w-3.5">{it.icon}</span>}
                <span className="min-w-0 flex-1 truncate">{it.label}</span>
                {it.count != null && it.count !== "" && <span className={cn("shrink-0 tabular-nums text-xs", it.muted ? "text-slate-300" : "text-slate-400")}>{it.count}</span>}
                {it.checked && <Check className="h-3.5 w-3.5 shrink-0 text-brand-700" />}
              </button>
            ),
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
